<script setup lang="ts">
/**
 * Корневой компонент приложения — глобальная обвязка редизайна:
 * фон (AppBackdrop), курсор-рамка (AppCursor), хедер/футер, полоса-переход
 * между экранами, палитра быстрой навигации (`/`) и тосты/global-loading.
 * Разметка и поведение — docs/design-handoff/README.md, разделы
 * «Интеракции и поведение» и DUSTAR-PRO-Redesign.dc.html.
 */

import { onBeforeUnmount, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useUIStore } from '@/stores/ui'
import { VToast } from '@/components/ui'
import AppBackdrop from '@/components/layout/AppBackdrop.vue'
import AppCursor from '@/components/layout/AppCursor.vue'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'
import NavPalette from '@/components/layout/NavPalette.vue'
import { useNavPalette } from '@/composables/useNavPalette'

const uiStore = useUIStore()
const route = useRoute()
const { toggle: togglePalette, close: closePalette } = useNavPalette()

/**
 * Клавиша `/` открывает палитру навигации (кроме фокуса в input/textarea),
 * Esc закрывает её. Ровно как в прототипе (componentDidMount → _onKey).
 */
function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape') {
    closePalette()
    return
  }
  const tag = (e.target as HTMLElement | null)?.tagName ?? ''
  if (e.key === '/' && tag !== 'INPUT' && tag !== 'TEXTAREA') {
    e.preventDefault()
    togglePalette()
  }
}

onMounted(() => {
  uiStore.loadTheme()
  window.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div id="app" class="app-shell">
    <AppBackdrop />
    <AppCursor />

    <!-- Переход между экранами: полоса акцента поверх контента -->
    <div :key="`sweep-${route.fullPath}`" class="app-route-sweep" aria-hidden="true"></div>

    <AppHeader />

    <!-- Переход между экранами: контент fade + translateY, .36s -->
    <main :key="route.fullPath" class="app-content">
      <router-view />
    </main>

    <AppFooter />

    <NavPalette />

    <!-- Toast Notifications -->
    <Teleport to="body">
      <div class="fixed bottom-4 right-4 z-50 space-y-2">
        <TransitionGroup
          enter-active-class="transition duration-300 ease-out"
          enter-from-class="opacity-0 translate-y-2"
          enter-to-class="opacity-100 translate-y-0"
          leave-active-class="transition duration-200 ease-in"
          leave-from-class="opacity-100 translate-y-0"
          leave-to-class="opacity-0 translate-y-2"
        >
          <VToast
            v-for="toast in uiStore.toasts"
            :key="toast.id"
            :toast="toast"
            @close="uiStore.removeToast"
          />
        </TransitionGroup>
      </div>
    </Teleport>

    <!-- Global Loading -->
    <Teleport to="body">
      <div
        v-if="uiStore.isGlobalLoading"
        class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center"
      >
        <div class="bg-surface shadow-lg p-6 flex items-center gap-3">
          <!-- rounded-full — осознанное исключение: это круглый спиннер, а не скруглённый угол -->
          <div class="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          <span class="text-text">Загрузка...</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style>
/* Импорт стилей */
@import './assets/styles/main.css';
</style>

<style scoped>
.app-shell {
  position: relative;
  z-index: 1;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

/* Контент режется свежим :key на каждый переход — CSS-анимация запускается
   сама, без ручного управления таймингом из JS. */
.app-content {
  flex: 1;
  animation: app-content-in 0.36s cubic-bezier(0.2, 0.7, 0.2, 1);
}
@keyframes app-content-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.app-route-sweep {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 210;
  pointer-events: none;
  background: var(--color-accent);
  transform: translateX(-100%);
  animation: app-route-sweep 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards;
}
@keyframes app-route-sweep {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(100%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .app-content,
  .app-route-sweep {
    animation: none;
  }
}
</style>
